import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
} from '@mui/material';
import {
  Facebook,
  Campaign,
  ExpandMore,
  ExpandLess,
  Assignment,
  DataObject,
} from '@mui/icons-material';

const FacebookCampaignInfo = ({ lead }) => {
  const [expanded, setExpanded] = useState(false);

  // Check if lead has Facebook campaign data
  const hasCampaignData = lead.campaign_id || lead.fb_lead_id;

  if (!hasCampaignData) {
    return null;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleString();
  };

  const getSourceIcon = (source) => {
    if (source === 'facebook_leads_retrieval') {
      return <Facebook color="primary" />;
    }
    return <DataObject color="action" />;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getSourceIcon(lead.source)}
            <Typography variant="h6">
              Campaign Information
            </Typography>
            <Chip
              label={lead.source === 'facebook_leads_retrieval' ? 'Facebook Lead' : 'Other Source'}
              color={lead.source === 'facebook_leads_retrieval' ? 'primary' : 'default'}
              size="small"
            />
          </Box>
          <IconButton
            onClick={() => setExpanded(!expanded)}
            aria-label="expand campaign info"
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Basic Campaign Info */}
        <Box sx={{ mb: 2 }}>
          {lead.campaign_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Campaign fontSize="small" color="primary" />
              <Typography variant="body2">
                <strong>Campaign:</strong> {lead.campaign_name}
              </Typography>
            </Box>
          )}

          {lead.ad_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Assignment fontSize="small" color="secondary" />
              <Typography variant="body2">
                <strong>Ad:</strong> {lead.ad_name}
              </Typography>
            </Box>
          )}

          {lead.fb_lead_id && (
            <Typography variant="caption" color="text.secondary">
              Facebook Lead ID: {lead.fb_lead_id}
            </Typography>
          )}
        </Box>

        {/* Expandable Detailed Info */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {/* Campaign & Ad IDs */}
            {(lead.campaign_id || lead.ad_id) && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Technical Details:</strong>
                </Typography>
                {lead.campaign_id && (
                  <Typography variant="caption" display="block">
                    Campaign ID: {lead.campaign_id}
                  </Typography>
                )}
                {lead.ad_id && (
                  <Typography variant="caption" display="block">
                    Ad ID: {lead.ad_id}
                  </Typography>
                )}
              </Alert>
            )}

            {/* Form Submission Data */}
            {lead.form_submission_data && lead.form_submission_data.field_data && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Form Submission Details
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Field Name</TableCell>
                        <TableCell>Submitted Value</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lead.form_submission_data.field_data.map((field, index) => (
                        <TableRow key={index}>
                          <TableCell sx={{ fontWeight: 'medium' }}>
                            {field.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </TableCell>
                          <TableCell>
                            {Array.isArray(field.values)
                              ? field.values.join(', ')
                              : field.values || 'N/A'
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {lead.form_submission_data.created_time && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Form submitted: {formatDate(lead.form_submission_data.created_time)}
                  </Typography>
                )}
              </Box>
            )}

            {/* Lead Source Details */}
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Lead Source Information
              </Typography>
              <Typography variant="body2">
                <strong>Source:</strong> {lead.source || 'Not specified'}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong> {formatDate(lead.created_at)}
              </Typography>
              {lead.last_contacted && (
                <Typography variant="body2">
                  <strong>Last Contacted:</strong> {formatDate(lead.last_contacted)}
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default FacebookCampaignInfo;